import {isFunction} from 'lodash';

import {UrlService} from '../services/Url';
import {envConfig} from 'config';
import {NotifyClientResult} from 'types';

import {logger} from './logger';

/**
 * NativeInterface is a global object created by Android/SDK webview to communicate with this application
 */
const NativeInterface = (window as any).NativeInterface;
const onEventHandlerName = 'onEvent';

const callNativeInterfaceMethod = ({name, description}, ...args): NotifyClientResult => {
    const methodInfo = {
        name,
        ...(name === onEventHandlerName && {description})
    };

    if (NativeInterface && isFunction(NativeInterface[name])) {
        try {
            NativeInterface[name](...args);
        } catch (e) {
            const message = `Unexpected error while execution NativeInterface method '${name}'`;
            logger.error({category: logger.category.NativeInterface, message}, e);
            return {...methodInfo, success: false, error: {message, details: e}};
        }
        return {...methodInfo, success: true};
    }

    let errorDetails = '';
    if (!NativeInterface) {
        errorDetails = '"NativeInterface" does not exist';
    } else if (!isFunction(NativeInterface[name])) {
        errorDetails = `"NativeInterface.${name}" is not a function`;
    }
    const errorMessage = 'NativeInterfaceError';
    logger.warn({category: logger.category.NativeInterface, message: `Unable to call method: '${name}'`}, args);
    return {...methodInfo, success: false, error: {message: errorMessage, details: errorDetails}};
};

const sendNativeInterfaceEvent = (name: string, data: any, options: {stringify: boolean}) => callNativeInterfaceMethod(
    {name: onEventHandlerName, description: name}, options?.stringify ? JSON.stringify({name, data}) : {name, data}
);

const callNativeInterfaceMethodWrapper = (name, ...args): NotifyClientResult =>
    callNativeInterfaceMethod({name, description: null}, ...args);

const formatNativeInterfaceEventResult = (result: NotifyClientResult): NotifyClientResult => ({
    name: onEventHandlerName,
    description: result.name,
    success: result.success,
    ...(result.error && {
        error: result.error
    })
});

const formatNativeInterfaceMethodResult = (result: NotifyClientResult): NotifyClientResult => ({
    name: result.name,
    success: result.success,
    ...(result.error && {
        error: result.error
    })
});

/**
 * Checks if app is opened from webview
 */
const isWebView = () => {
    // Temporary solution to allow to make testing from any place without NativeInterface
    const skipNativeInterface = UrlService.getSearchParamValue('skipNativeInterface');
    return skipNativeInterface === 'true' || envConfig.isDevelopmentMode || !!NativeInterface;
};

export const nativeInterfaceUtils = {
    callNativeInterfaceMethodWrapper,
    sendNativeInterfaceEvent,
    formatNativeInterfaceEventResult,
    formatNativeInterfaceMethodResult,
    isWebView,
};